import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import { TrendingUp, Target, Trophy, Activity, BarChart3, ShieldCheck, Sparkles, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

type Stats = {
  wins: number;
  losses: number;
  pushes: number;
  units: number;
  roi: number;
  total_picks: number;
};

type TimelinePoint = { d: string; cumulative_units: number };
type TopEdge = {
  id: string;
  title: string;
  sport_code: string | null;
  confidence_score: number | null;
  unit_rating: number | null;
} | null;

const EMPTY: Stats = { wins: 0, losses: 0, pushes: 0, units: 0, roi: 0, total_picks: 0 };

function fmtUnits(u: number) {
  const sign = u > 0 ? "+" : u < 0 ? "" : "";
  return `${sign}${u.toFixed(2)}u`;
}
function fmtRoi(r: number) {
  const sign = r > 0 ? "+" : "";
  return `${sign}${r.toFixed(1)}%`;
}

export default function PlatformPerformance() {
  const [stats, setStats] = useState<Stats>(EMPTY);
  const [timeline, setTimeline] = useState<TimelinePoint[]>([]);
  const [topEdge, setTopEdge] = useState<TopEdge>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      const [statsRes, tlRes, edgeRes] = await Promise.all([
        supabase.rpc("record_stats", {
          _uid: null,
          _sport_code: null,
          _bet_type: null,
          _start_ts: null,
          _end_ts: null,
        }),
        supabase.rpc("public_units_timeline"),
        supabase.rpc("public_top_edge_today"),
      ]);
      const row: any = statsRes.data?.[0];
      if (row) {
        setStats({
          wins: Number(row.wins ?? 0),
          losses: Number(row.losses ?? 0),
          pushes: Number(row.pushes ?? 0),
          units: Number(row.units ?? 0),
          roi: Number(row.roi ?? 0),
          total_picks: Number(row.total_picks ?? 0),
        });
      }
      if (tlRes.data) {
        setTimeline(
          (tlRes.data as any[]).map((p) => ({
            d: String(p.d),
            cumulative_units: Number(p.cumulative_units ?? 0),
          })),
        );
      }
      if (edgeRes.data && (edgeRes.data as any[])[0]) {
        setTopEdge((edgeRes.data as any[])[0] as any);
      }
      setLoaded(true);
    })();
  }, []);

  const decided = stats.wins + stats.losses;
  const winRate = decided > 0 ? (stats.wins / decided) * 100 : 0;
  const positive = stats.units >= 0;

  // Pseudo-edge from confidence: maps 60→2.0% .. 95→6.0%
  const edgePct =
    topEdge?.confidence_score != null
      ? Math.max(0.5, ((Number(topEdge.confidence_score) - 55) / 40) * 5).toFixed(1)
      : null;

  return (
    <section
      aria-labelledby="platform-perf"
      className="container pb-12 md:pb-16 -mt-2 md:-mt-6"
    >
      <div className="relative">
        {/* ambient glows */}
        <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden rounded-3xl">
          <div className="absolute -top-24 left-1/4 h-72 w-72 rounded-full bg-primary/15 blur-3xl" />
          <div className="absolute -bottom-24 right-1/4 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />
        </div>

        <div className="te-card backdrop-blur-xl bg-card/60 border-border/60 p-5 md:p-8 lg:p-10 rounded-3xl">
          {/* Header */}
          <div className="flex flex-wrap items-end justify-between gap-3 mb-6 md:mb-8">
            <div className="min-w-0">
              <span className="te-chip border border-primary/30 bg-primary/10 text-primary mb-3">
                <ShieldCheck className="w-3 h-3" /> Live platform performance
              </span>
              <h2
                id="platform-perf"
                className="font-display text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight"
              >
                The numbers, <span className="te-grad-text">in the open.</span>
              </h2>
              <p className="text-sm md:text-base text-muted-foreground mt-2 max-w-2xl">
                Every pick tracked publicly and graded automatically.
              </p>
              <p className="text-xs md:text-sm text-muted-foreground/80 mt-1 max-w-2xl">
                No fake records. No inflated edges. Fully transparent tracking.
              </p>
            </div>
            <Link
              to="/history"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
            >
              View full history <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-12 gap-3 md:gap-4">
            <StatCard
              className="md:col-span-4"
              size="xl"
              icon={TrendingUp}
              label="Units"
              value={fmtUnits(stats.units)}
              accent={positive ? "primary" : "danger"}
              loaded={loaded}
            />
            <StatCard
              className="md:col-span-4"
              size="xl"
              icon={BarChart3}
              label="ROI"
              value={fmtRoi(stats.roi)}
              accent={stats.roi >= 0 ? "primary" : "danger"}
              loaded={loaded}
            />
            <StatCard
              className="md:col-span-4"
              size="lg"
              icon={Target}
              label="Win Rate"
              value={`${winRate.toFixed(1)}%`}
              loaded={loaded}
            />
            <StatCard
              className="col-span-2 md:col-span-7"
              size="lg"
              icon={Trophy}
              label="Record"
              value={`${stats.wins}-${stats.losses}${stats.pushes ? `-${stats.pushes}` : ""}`}
              hint="Wins · Losses · Pushes"
              loaded={loaded}
            />
            <StatCard
              className="col-span-2 md:col-span-5"
              size="sm"
              icon={Activity}
              label="Total Picks"
              value={`${stats.total_picks}`}
              muted
              loaded={loaded}
            />
          </div>

          {/* Chart + Top Edge */}
          <div className="mt-5 md:mt-6 grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
            <div className="lg:col-span-2 rounded-2xl border border-border/60 bg-secondary/20 p-4 md:p-5 overflow-hidden">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[10px] sm:text-xs uppercase text-muted-foreground tracking-wider">
                    Cumulative units
                  </div>
                  <div className="font-display text-lg md:text-xl font-bold">
                    <span className={cn(positive ? "te-grad-text" : "text-destructive")}>
                      {fmtUnits(stats.units)}
                    </span>
                    <span className="text-muted-foreground text-sm font-normal ml-2">
                      all-time
                    </span>
                  </div>
                </div>
                <div className="hidden sm:inline-flex te-chip border border-primary/20 bg-primary/5 text-primary">
                  <TrendingUp className="w-3 h-3" /> Public ledger
                </div>
              </div>
              <UnitsChart data={timeline} positive={positive} />
            </div>

            <TopEdgeCard topEdge={topEdge} edgePct={edgePct} loaded={loaded} />
          </div>
        </div>
      </div>
    </section>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
  accent,
  muted,
  size = "lg",
  className,
  loaded,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  hint?: string;
  accent?: "primary" | "danger";
  muted?: boolean;
  size?: "sm" | "lg" | "xl";
  className?: string;
  loaded: boolean;
}) {
  const valueSize =
    size === "xl"
      ? "text-2xl sm:text-4xl md:text-5xl"
      : size === "lg"
      ? "text-2xl sm:text-3xl md:text-4xl"
      : "text-xl sm:text-2xl md:text-3xl";
  return (
    <div
      className={cn(
        "group relative rounded-2xl border border-border/60 p-4 md:p-5 overflow-hidden",
        "bg-gradient-to-br from-secondary/40 via-card/60 to-card/30 backdrop-blur-sm",
        "transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/40",
        accent === "primary" && "hover:shadow-glow",
        muted && "opacity-90",
        className,
      )}
    >
      <div
        className={cn(
          "pointer-events-none absolute -top-10 -right-10 h-32 w-32 rounded-full blur-2xl transition-opacity duration-500",
          accent === "primary" && "bg-primary/20 opacity-60 group-hover:opacity-100",
          accent === "danger" && "bg-destructive/20 opacity-60",
          !accent && "bg-primary/5 opacity-40 group-hover:opacity-70",
        )}
      />
      <div className="flex items-center justify-between gap-2 relative">
        <span className="text-[10px] sm:text-xs uppercase text-muted-foreground tracking-wider font-semibold">
          {label}
        </span>
        <Icon
          className={cn(
            "w-4 h-4",
            accent === "primary" ? "text-primary" : accent === "danger" ? "text-destructive" : "text-muted-foreground",
          )}
        />
      </div>
      <div
        className={cn(
          "font-display font-bold mt-2 tabular-nums tracking-tight relative",
          valueSize,
          accent === "primary" && "te-grad-text",
          accent === "danger" && "text-destructive",
          !accent && "text-foreground",
          loaded ? "animate-fade-in" : "opacity-0",
        )}
      >
        {value}
      </div>
      {hint && (
        <div className="text-[10px] sm:text-xs text-muted-foreground mt-1 relative">{hint}</div>
      )}
    </div>
  );
}

function UnitsChart({ data, positive }: { data: TimelinePoint[]; positive: boolean }) {
  const stroke = positive ? "hsl(var(--primary))" : "hsl(var(--destructive))";
  return (
    <div className="h-[200px] sm:h-[240px] md:h-[280px] w-full animate-fade-in">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 10, right: 8, left: -10, bottom: 0 }}>
          <defs>
            <linearGradient id="unitsFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={stroke} stopOpacity={0.45} />
              <stop offset="100%" stopColor={stroke} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="hsl(var(--border))" strokeOpacity={0.4} vertical={false} />
          <XAxis
            dataKey="d"
            tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: "hsl(var(--border))" }}
            minTickGap={32}
            tickFormatter={(v) => {
              const dt = new Date(v);
              return isNaN(dt.getTime())
                ? v
                : dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
            }}
          />
          <YAxis
            tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            width={36}
            tickFormatter={(v) => `${v}u`}
          />
          <Tooltip
            cursor={{ stroke: "hsl(var(--primary))", strokeOpacity: 0.3 }}
            contentStyle={{
              background: "hsl(var(--popover))",
              border: "1px solid hsl(var(--border))",
              borderRadius: 12,
              fontSize: 12,
            }}
            labelFormatter={(v) => {
              const dt = new Date(v as string);
              return isNaN(dt.getTime())
                ? String(v)
                : dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
            }}
            formatter={(val: any) => [`${Number(val).toFixed(2)}u`, "Cumulative"]}
          />
          <Area
            type="monotone"
            dataKey="cumulative_units"
            stroke={stroke}
            strokeWidth={2.5}
            fill="url(#unitsFill)"
            isAnimationActive
            animationDuration={1200}
            animationEasing="ease-out"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function TopEdgeCard({
  topEdge,
  edgePct,
  loaded,
}: {
  topEdge: TopEdge;
  edgePct: string | null;
  loaded: boolean;
}) {
  return (
    <div
      className={cn(
        "relative rounded-2xl border border-primary/30 p-5 overflow-hidden",
        "bg-gradient-to-br from-primary/10 via-card/70 to-card/30 backdrop-blur-sm",
        "transition-all duration-300 hover:border-primary/50 hover:shadow-glow",
      )}
    >
      <div className="pointer-events-none absolute -top-12 -right-12 h-40 w-40 rounded-full bg-primary/20 blur-3xl" />
      <div className="flex items-center justify-between mb-2 relative">
        <span className="te-chip border border-primary/30 bg-primary/10 text-primary">
          <Sparkles className="w-3 h-3" /> Today's Top Edge
        </span>
        {topEdge?.sport_code && (
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
            {topEdge.sport_code}
          </span>
        )}
      </div>
      {topEdge ? (
        <div className={cn("relative", loaded ? "animate-fade-in" : "opacity-0")}>
          <div className="font-display text-lg md:text-xl font-bold leading-snug mt-1 line-clamp-2">
            {topEdge.title}
          </div>
          <div className="mt-4 flex items-end justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase text-muted-foreground tracking-wider">
                Edge
              </div>
              <div className="font-display text-3xl md:text-4xl font-bold te-grad-text tabular-nums">
                +{edgePct ?? "—"}%
              </div>
            </div>
            {topEdge.unit_rating != null && (
              <div className="text-right">
                <div className="text-[10px] uppercase text-muted-foreground tracking-wider">
                  Units
                </div>
                <div className="font-display text-xl font-bold">
                  {Number(topEdge.unit_rating).toFixed(1)}u
                </div>
              </div>
            )}
          </div>
          <Link
            to="/auth"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline relative"
          >
            See full breakdown <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <div className="relative">
          <div className="font-display text-base md:text-lg font-bold leading-snug mt-1 text-muted-foreground">
            New edge drops before first pitch.
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Today's Top Edge appears here once published.
          </p>
          <Link
            to="/auth"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
          >
            Get notified <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}
    </div>
  );
}
